import { useContext, useEffect, useMemo, useState } from 'react';

import { Delete, Edit, Print } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    ThemeProvider,
    Tooltip,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';

import * as AssociadoVinculoAPI from '../../../api/associadoVinculo';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { useMRTState } from '../../../hooks/useMRTState';
import { podeCancelar, podeEditar, podeImprimir, podeIncluir } from '../../../hooks/usePermisionMRT';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { obterPermissoesUsuario } from '../../../utils/funcoes';
import tableThemeCustom from '../../form/tableThemeCustom';

function formatarData(value) {
    if (!value) return '';

    const [ano, mes, dia] = String(value).slice(0, 10).split('-');

    return ano && mes && dia ? `${dia}/${mes}/${ano}` : '';
}

function formatarDataHora(value) {
    if (!value) return '';

    const data = new Date(value);

    return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function simNao(value) {
    return value ? 'Sim' : 'Não';
}

function Index() {
    const { parametro } = useContext(Context);
    const { pessoaId, empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const urlParams = new URLSearchParams(window.location.search);
    const ul = urlParams.get('ul');
    const li = urlParams.get('li');

    const [token] = useState(localStorage.getItem('token') || '');
    const [dados, setDados] = useState([]);
    const [vinculoExcluir, setVinculoExcluir] = useState(null);
    const [excluindo, setExcluindo] = useState(false);
    const [imprimindoUuid, setImprimindoUuid] = useState(null);
    const [imprimindoLista, setImprimindoLista] = useState(false);

    const mrt = useMRTState({
        storageKey: 'associado-vinculo.index',
        initialState: {
            columnFilterFns: {},
            columnFilters: [],
            globalFilter: '',
            sorting: [],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const {
        columnFilterFns,
        columnFilters,
        globalFilter,
        sorting,
        pagination,
        setIsLoading,
        setIsError,
        setIsRefetching,
        setRowCount,
    } = mrt;

    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
    });

    async function fetchData() {
        dados.length ? setIsRefetching(true) : setIsLoading(true);

        try {
            const response = await AssociadoVinculoAPI.listar({
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(columnFilters),
                filtersMode: JSON.stringify(columnFilterFns),
                globalFilter: globalFilter || '',
                sorting: JSON.stringify(sorting),
            });

            setDados(response.data.data || []);
            setRowCount(Number(response.data.meta?.totalRowCount) || 0);
            setIsError(false);
        } catch (error) {
            setIsError(true);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar os vínculos dos associados.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
            return;
        }

        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, columnFilterFns, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);

    useEffect(() => {
        if (!pessoaId || !empresaId || !ul || !li) return;

        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [pessoaId, empresaId, ul, li]);

    function novoVinculo() {
        const params = new URLSearchParams(urlParams);

        params.delete('uuid');
        params.set('btnText', 'Cadastrar');

        navigate('/AssociadoVinculos/CreateOrEdit', params);
    }

    function editarVinculo(vinculo) {
        const params = new URLSearchParams(urlParams);

        params.set('uuid', vinculo.uuid);
        params.set('btnText', 'Atualizar');

        navigate('/AssociadoVinculos/CreateOrEdit', params);
    }

    async function imprimirFicha(vinculo) {
        if (!vinculo?.uuid || imprimindoUuid) return;

        const janela = window.open('', '_blank');

        if (!janela) {
            setFlashMessage('O navegador bloqueou a abertura da ficha. Permita pop-ups para este sistema.', 'warning');
            return;
        }

        setImprimindoUuid(vinculo.uuid);

        try {
            janela.document.title = 'Gerando ficha do associado...';
            janela.document.body.innerHTML = '<p style="font-family: sans-serif">Gerando ficha do associado...</p>';

            const response = await AssociadoVinculoAPI.ficha(vinculo.uuid);
            const arquivo = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(arquivo);

            janela.location.href = url;

            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (error) {
            janela.close();

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível gerar a ficha do associado.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setImprimindoUuid(null);
        }
    }

    async function imprimirListagem() {
        if (imprimindoLista) return;

        const janela = window.open('', '_blank');

        if (!janela) {
            setFlashMessage('O navegador bloqueou a abertura do relatório. Permita pop-ups para este sistema.', 'warning');
            return;
        }

        setImprimindoLista(true);

        try {
            janela.document.title = 'Gerando relatório de associados...';
            janela.document.body.innerHTML = '<p style="font-family: sans-serif">Gerando relatório de associados...</p>';

            const params = new URLSearchParams({
                filters: JSON.stringify(columnFilters),
                filtersMode: JSON.stringify(columnFilterFns),
                globalFilter: globalFilter || '',
                sorting: JSON.stringify(sorting),
            });

            const response = await AssociadoVinculoAPI.imprimir(params);
            const arquivo = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(arquivo);

            janela.location.href = url;

            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (error) {
            janela.close();

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível imprimir a listagem dos associados.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setImprimindoLista(false);
        }
    }

    async function confirmarExclusao() {
        if (!vinculoExcluir?.uuid || excluindo) return;

        setExcluindo(true);

        try {
            const response = await AssociadoVinculoAPI.excluir(vinculoExcluir.uuid);

            setVinculoExcluir(null);
            setFlashMessage(response.data.message, response.data.type);
            await fetchData();
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível excluir o vínculo do associado.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setExcluindo(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                id: 'associadoVinculos',
                header: 'VÍNCULOS DOS ASSOCIADOS',
                columns: [
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                    {
                        accessorKey: 'Pessoa.descricao',
                        header: 'Pessoa',
                    },
                    {
                        accessorKey: 'Pessoa.CPForCNPJ',
                        header: 'CPF/CNPJ',
                    },
                    {
                        accessorKey: 'dataInicio',
                        header: 'Início',
                        Cell: ({ cell }) => formatarData(cell.getValue()),
                    },
                    {
                        accessorKey: 'dataFim',
                        header: 'Término',
                        Cell: ({ cell }) => formatarData(cell.getValue()),
                    },
                    {
                        accessorKey: 'motivoDesligamento',
                        header: 'Motivo do desligamento',
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'updatedAt',
                        header: 'Atualizado em',
                        Cell: ({ cell }) => formatarDataHora(cell.getValue()),
                    },
                ],
            },
        ],
        []
    );

    const theme = tableThemeCustom();

    return (
        <div>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    renderTopToolbarCustomActions={() => (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button color="secondary" variant="contained" disabled={!podeIncluir()} onClick={novoVinculo}>
                                Novo
                            </Button>

                            <Button
                                color="info"
                                variant="contained"
                                startIcon={<Print />}
                                disabled={!podeImprimir() || imprimindoLista}
                                onClick={imprimirListagem}
                            >
                                {imprimindoLista ? 'Gerando...' : 'Imprimir'}
                            </Button>
                        </Box>
                    )}
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Imprimir ficha do associado">
                                <span>
                                    <IconButton
                                        color="secondary"
                                        disabled={!podeImprimir() || Boolean(imprimindoUuid)}
                                        onClick={() => imprimirFicha(row.original)}
                                    >
                                        <Print />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Editar vínculo do associado">
                                <span>
                                    <IconButton
                                        color="primary"
                                        disabled={!podeEditar()}
                                        onClick={() => editarVinculo(row.original)}
                                    >
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Excluir vínculo do associado">
                                <span>
                                    <IconButton
                                        color="error"
                                        disabled={!podeCancelar()}
                                        onClick={() => setVinculoExcluir(row.original)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                />
            </ThemeProvider>

            <Dialog
                open={Boolean(vinculoExcluir)}
                onClose={() => {
                    if (!excluindo) setVinculoExcluir(null);
                }}
            >
                <DialogTitle>Excluir vínculo do associado?</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        O vínculo de <strong>{vinculoExcluir?.Pessoa?.descricao}</strong>, iniciado em{' '}
                        <strong>{formatarData(vinculoExcluir?.dataInicio)}</strong>, será excluído.
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button disabled={excluindo} onClick={() => setVinculoExcluir(null)}>
                        Cancelar
                    </Button>

                    <Button variant="contained" color="error" disabled={excluindo} onClick={confirmarExclusao}>
                        {excluindo ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Index;
